import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Activity, TrendingUp, Database, GitBranch, ListChecks } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const Metric = ({ label, value, sub, testid }) => (
  <div className="ek-surface-2 border ek-line rounded-lg p-4" data-testid={testid}>
    <div className="text-[26px] font-bold ek-text leading-none tabular-nums">{value}</div>
    <div className="text-[12px] ek-text-soft mt-1.5 leading-tight">{label}</div>
    {sub && <div className="text-[11px] ek-text-muted mt-1">{sub}</div>}
  </div>
);

export default function ProofDashboardPanel() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/enterprise360/proof-dashboard");
      setD(data);
    } catch {
      toast.error("Could not load the proof dashboard");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const e = d?.evidence;
  const c = d?.commercial;
  const winRate = c && c.opportunities_total ? Math.round((c.opportunities_won / c.opportunities_total) * 100) : 0;
  const enqDelta = c ? c.enquiries_this_month - c.enquiries_last_month : 0;

  return (
    <div className="space-y-6" data-testid="proof-dashboard-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold ek-text flex items-center gap-2">
            <Activity size={18} className="text-[var(--e360-primary,#7737F6)]" /> Proof flywheel &amp; leading indicators
          </h2>
          <p className="text-[13px] ek-text-soft mt-1 max-w-3xl">
            The defensibility of the System of Proof compounds with usage. This is the proof currency
            accruing — plus the commercial leading indicators that show the model working before outcome
            data matures.
          </p>
        </div>
        <button onClick={load} disabled={loading} data-testid="proof-dashboard-refresh"
          className="inline-flex items-center gap-1 text-[12px] ek-text-muted hover:opacity-70 transition-opacity">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {loading && !d ? <div className="text-[13px] ek-text-muted py-6">Loading…</div> : d && (
        <>
          <div>
            <div className="flex items-center gap-2 text-[13px] font-semibold ek-text-soft mb-3">
              <Database size={14} /> Evidence base (the time &amp; engagements currency)
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Metric label="Live engagements" value={e.engagements_real} sub={`${e.engagements_total} incl. demos`} testid="pd-engagements" />
              <Metric label="Archetypes calibrated (v1)" value={`${e.archetypes_calibrated}/${e.archetypes_total}`} sub={`${e.archetypes_field_validated} field-validated`} testid="pd-calibrated" />
              <Metric label="Assessment runs" value={e.assessment_runs_total} sub={`${e.assessment_runs_completed} completed`} testid="pd-runs" />
              <Metric label="Validation predictions" value={e.validation_predictions_captured} sub="captured for back-testing" testid="pd-predictions" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              <Metric label="Benchmark cohort" value={e.benchmark_contributions} sub="anonymised contributions" testid="pd-benchmark" />
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 text-[13px] font-semibold ek-text-soft mb-3">
              <TrendingUp size={14} /> Commercial leading indicators
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Metric label="Enquiries this month" value={c.enquiries_this_month}
                sub={`${enqDelta >= 0 ? "+" : ""}${enqDelta} vs last month`} testid="pd-enquiries" />
              <Metric label="Open opportunities" value={c.opportunities_total} testid="pd-opps" />
              <Metric label="Won" value={c.opportunities_won} testid="pd-won" />
              <Metric label="Win rate" value={`${winRate}%`} sub="of tracked opportunities" testid="pd-winrate" />
            </div>
            {c.opportunities_by_stage && (
              <div className="ek-surface-2 border ek-line rounded-lg p-4 mt-3" data-testid="pd-pipeline">
                <div className="text-[12px] ek-text-soft mb-2 flex items-center gap-1"><GitBranch size={12} /> Pipeline by stage</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(c.opportunities_by_stage).map(([k, v]) => (
                    <span key={k} className="inline-flex items-center gap-1 text-[12px] ek-surface border ek-line rounded-full px-3 py-1">
                      {k} <b className="tabular-nums">{v}</b>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {c.tom_delivery && c.tom_delivery.promoted > 0 && (
              <div className="ek-surface-2 border ek-line rounded-lg p-4 mt-3" data-testid="pd-tom-delivery">
                <div className="text-[12px] ek-text-soft mb-2 flex items-center gap-1">
                  <ListChecks size={12} /> TOM advice → accountable delivery
                </div>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-2xl font-semibold text-[#7737F6] tabular-nums" data-testid="pd-tom-promoted">{c.tom_delivery.promoted}</span>
                  <span className="text-[12px] ek-text-muted">delivery moves promoted into RAID</span>
                  <span className="ek-text-muted">→</span>
                  <span className="text-2xl font-semibold text-emerald-600 tabular-nums" data-testid="pd-tom-closed">{c.tom_delivery.closed}</span>
                  <span className="text-[12px] ek-text-muted">closed ({c.tom_delivery.pct_closed}%)</span>
                </div>
                <p className="text-[11px] ek-text-muted mt-1.5 italic">Proof that the operating-model engine's recommendations convert into tracked, completed delivery — not slideware.</p>
              </div>
            )}

            {c.funnel && c.funnel.length > 0 && (() => {
              const maxCount = Math.max(...c.funnel.map((f) => f.count), 1);
              return (
                <div className="ek-surface-2 border ek-line rounded-lg p-4 mt-3" data-testid="pd-funnel">
                  <div className="text-[12px] ek-text-soft mb-3 flex items-center gap-1">
                    <TrendingUp size={12} /> Conversion funnel <span className="ek-text-muted">(indicative volumes — showcase → closed-won)</span>
                  </div>
                  <div className="space-y-2">
                    {c.funnel.map((f, i) => (
                      <div key={f.stage} data-testid={`pd-funnel-${i}`} className="flex items-center gap-3">
                        <div className="w-40 shrink-0 text-[12px] ek-text-soft">{f.stage}</div>
                        <div className="flex-1 h-6 rounded ek-surface overflow-hidden border ek-line">
                          <div className="h-6 bg-[#7737F6] rounded-r flex items-center px-2"
                               style={{ width: `${Math.max((f.count / maxCount) * 100, f.count ? 6 : 0)}%` }}>
                            <span className="text-[11px] font-semibold text-white tabular-nums">{f.count}</span>
                          </div>
                        </div>
                        <div className="w-16 shrink-0 text-right text-[11px] ek-text-muted tabular-nums" data-testid={`pd-funnel-conv-${i}`}>
                          {f.step_conversion_pct != null ? `${f.step_conversion_pct}%` : "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] ek-text-muted mt-2 italic">Right column = step-to-step conversion. Leading-indicator volumes, not per-lead attribution.</p>
                </div>
              );
            })()}
          </div>

          <p className="text-[12px] ek-text-muted italic border-l-2 border-[var(--e360-primary,#7737F6)] pl-3">{d.flywheel_note}</p>

          {d.economics && (
            <div data-testid="pd-economics">
              <div className="flex items-center gap-2 text-[13px] font-semibold ek-text-soft mb-3">
                <Activity size={14} /> Consulting economics — senior-consultant effort saved
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                <Metric label="Traditional effort / engagement" value={`${d.economics.per_engagement.traditional_days}d`} testid="pd-econ-trad" />
                <Metric label="Engine-assisted effort" value={`${d.economics.per_engagement.engine_days}d`} sub={`${d.economics.per_engagement.saving_pct}% saved`} testid="pd-econ-engine" />
                <Metric label="Cumulative days saved" value={`${d.economics.cumulative.saving_days}d`} sub={`across ${d.economics.cumulative.engagements} engagement(s)`} testid="pd-econ-cumulative" />
              </div>
              <div className="ek-surface border ek-line rounded-lg overflow-hidden">
                <table className="w-full text-[13px]" data-testid="pd-economics-table">
                  <thead><tr className="ek-surface-2 text-left ek-text-soft">
                    <th className="p-3 font-medium">Artefact</th><th className="p-3 font-medium">Traditional</th>
                    <th className="p-3 font-medium">With engine</th><th className="p-3 font-medium">Saved</th></tr></thead>
                  <tbody>
                    {d.economics.rows.map((r, i) => (
                      <tr key={i} className="border-t ek-line">
                        <td className="p-3 ek-text">{r.artefact}</td>
                        <td className="p-3 ek-text-muted tabular-nums">{r.traditional_days}d</td>
                        <td className="p-3 ek-text-muted tabular-nums">{r.engine_days}d</td>
                        <td className="p-3 text-emerald-700 font-medium tabular-nums">{r.saving_days}d ({r.saving_pct}%)</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] ek-text-muted mt-2 italic">{d.economics.note}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
